import { request } from "@/utilities/libs/request.ts";
import { IResponseData, ListResponseData } from "@/utilities/types/requests.ts";
import { ERequestMethods } from "@/utilities/enums/RequestMethod.ts";
import {
  ICNameTargetDomains,
  IDomainDTO,
  ISectionConfig,
  TGoogleAdsAccount,
  TGoogleAdsAccountCreate,
  TGoogleAdsAccountUpdate,
  TAgencySetting,
  TAgencySettingCreate,
  TAgencySettingUpdate,
  TDomain,
} from "../types/Setting";
import { GoogleDataManagerEventLog } from "../types/GoogleDataManager";
import { E_SETTINGS } from "../enums/Settings";
import { SUFFIX_DOMAIN } from "@/config/constants";
import {
  emailToSubdomain,
  toValidSubdomain,
} from "@/utils/email-to-valid-subdmain";

function getAgencySetting() {
  return request<IResponseData<TAgencySetting>>({
    method: ERequestMethods.GET,
    url: E_SETTINGS.AGENCY,
  });
}

function checkAvailableDomain(domain: string, configId?: string) {
  return request<
    IResponseData<{
      data: null;
      message: string;
      statusCode: number;
      success: boolean;
    }>
  >({
    method: ERequestMethods.GET,
    url: `${E_SETTINGS.CHECK_DOMAIN}`,
    params: { domainName: domain, configId },
  });
}

function updateAgencySetting(id: string, data: Partial<TAgencySettingUpdate>) {
  return request<IResponseData<TAgencySetting>>({
    method: ERequestMethods.PATCH,
    url: E_SETTINGS.UPDATE_AGENCY.replace(":id", id),
    data,
  });
}

function createAgencySetting(data: TAgencySettingCreate) {
  return request<IResponseData<TAgencySetting>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.CREATE_AGENCY,
    data,
  });
}

function getGoogleAdsAccounts() {
  return request<IResponseData<TGoogleAdsAccount[]>>({
    method: ERequestMethods.GET,
    url: E_SETTINGS.GOOGLE_ADS_ACCOUNTS,
  });
}

function createGoogleAdsAccount(data: TGoogleAdsAccountCreate) {
  return request<IResponseData<TGoogleAdsAccount>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.GOOGLE_ADS_ACCOUNTS,
    data,
  });
}

function updateGoogleAdsAccount(
  id: string,
  data: TGoogleAdsAccountUpdate,
) {
  return request<IResponseData<TGoogleAdsAccount>>({
    method: ERequestMethods.PATCH,
    url: E_SETTINGS.GOOGLE_ADS_ACCOUNT_ID(id),
    data,
  });
}

function deleteGoogleAdsAccount(id: string) {
  return request<IResponseData<void>>({
    method: ERequestMethods.DELETE,
    url: E_SETTINGS.GOOGLE_ADS_ACCOUNT_ID(id),
  });
}

function getGoogleDataManagerLogs(params: Record<string, string | number> = {}) {
  return request<ListResponseData<GoogleDataManagerEventLog>>({
    method: ERequestMethods.GET,
    url: "/google-data-manager/logs",
    params,
  });
}

function getGoogleDataManagerLog(id: string) {
  return request<IResponseData<GoogleDataManagerEventLog>>({
    method: ERequestMethods.GET,
    url: `/google-data-manager/logs/${id}`,
  });
}

function retryGoogleDataManagerLog(id: string) {
  return request<IResponseData<GoogleDataManagerEventLog>>({
    method: ERequestMethods.POST,
    url: `/google-data-manager/logs/${id}/retry`,
  });
}

function getAllConfigLocations() {
  return request<ListResponseData<ISectionConfig>>({
    method: ERequestMethods.GET,
    url: E_SETTINGS.CONFIG_LOCATION,
    params: {
      limit: 50,
      page: 1,
    },
  });
}

function addConfigLocation(data: any) {
  return request<IResponseData<ISectionConfig>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.CONFIG_LOCATION,
    data,
  });
}

function updateConfigLocation(id: string, data: any) {
  return request<IResponseData<ISectionConfig>>({
    method: ERequestMethods.PATCH,
    url: E_SETTINGS.CONFIG_LOCATION_ID(id),
    data,
  });
}

function updateDomain(id: string, data: any) {
  return request<IResponseData<TDomain>>({
    method: ERequestMethods.PATCH,
    url: E_SETTINGS.UPDATE_DOMAIN(id),
    data,
  });
}

function createDomain(data: IDomainDTO) {
  return request<IResponseData<TDomain>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.CREATE_DOMAIN,
    data,
  });
}

function deleteDomain(id: string) {
  return request<IResponseData<void>>({
    method: ERequestMethods.DELETE,
    url: E_SETTINGS.DELETE_DOMAIN(id),
  });
}

function generateUniqeDomain(projectId: string) {
  return request<IResponseData<string>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.GENERATE_UNIQUE_DOMAIN,
    data: { projectId },
  });
}

function generateUniqueCNameDomain(body: {
  userId?: string;
  projectId: string;
}) {
  return request<IResponseData<string>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.GENERATE_UNIQUE_CNAME_DOMAIN,
    data: body,
  });
}

function generateUniqueCNameDomains(body: {
  userId?: string;
  projectId: string;
}) {
  return request<IResponseData<ICNameTargetDomains>>({
    method: ERequestMethods.POST,
    url: E_SETTINGS.GENERATE_UNIQUE_CNAME_DOMAINS,
    data: body,
  });
}

async function generateUniqueAgencySubdomain(
  userId: string,
  email: string,
  errorCount = 0,
) {
  const randomString = Math.random().toString(36).substring(2, 15);
  const emailSubdomain = emailToSubdomain(email);
  const subdomain =
    `${randomString}-${emailSubdomain.toLocaleLowerCase()}-${userId.slice(0, 4)}${SUFFIX_DOMAIN}`.replace(
      /\s/g,
      "-",
    );
  try {
    await checkAvailableDomain(toValidSubdomain(subdomain));
    return subdomain;
  } catch (error) {
    if (errorCount < 5) {
      await generateUniqueAgencySubdomain(userId, email, errorCount + 1);
    } else {
      throw error;
    }
  }
}

const SettingsRepository = Object.freeze({
  getAgencySetting,
  updateAgencySetting,
  createAgencySetting,
  getGoogleAdsAccounts,
  createGoogleAdsAccount,
  updateGoogleAdsAccount,
  deleteGoogleAdsAccount,
  getGoogleDataManagerLogs,
  getGoogleDataManagerLog,
  retryGoogleDataManagerLog,
  getAllConfigLocations,
  addConfigLocation,
  updateConfigLocation,
  checkAvailableDomain,
  updateDomain,
  createDomain,
  deleteDomain,
  generateUniqeDomain,
  generateUniqueAgencySubdomain,
  generateUniqueCNameDomain,
  generateUniqueCNameDomains,
});

export default SettingsRepository;
